import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { formatUsd } from "../utils/format";
import { Skeleton } from "./Skeleton";

interface WalletPrizeHistoryProps {
  walletAddress: string;
}

const PAGE_SIZES = [10, 25, 50, 100];

/**
 * Paginated, filterable prize history for a wallet. Filters (machine, date
 * range) and pagination are applied server-side so heavy wallets stay light.
 */
export function WalletPrizeHistory({ walletAddress }: WalletPrizeHistoryProps) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [gachaCode, setGachaCode] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // Date inputs are YYYY-MM-DD; treat them as whole UTC days
  const fromIso = from ? `${from}T00:00:00.000Z` : undefined;
  const toIso = to ? `${to}T23:59:59.999Z` : undefined;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["wallet-prizes", walletAddress, page, limit, gachaCode, fromIso, toIso],
    queryFn: async () => {
      const { data, error } = await api.newWinnerEvents
        .wallet({ walletAddress })
        .get({
          query: {
            page,
            limit,
            ...(gachaCode ? { gachaCode } : {}),
            ...(fromIso ? { from: fromIso } : {}),
            ...(toIso ? { to: toIso } : {}),
          },
        });
      if (error) throw error;
      return data;
    },
    enabled: !!walletAddress,
    placeholderData: keepPreviousData,
  });

  const hasFilter = Boolean(gachaCode || from || to);
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / limit));
  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, total);

  const updateFilter = (apply: () => void) => {
    apply();
    setPage(1);
  };

  const clearFilters = () =>
    updateFilter(() => {
      setGachaCode("");
      setFrom("");
      setTo("");
    });

  if (isLoading) {
    return <Skeleton kind="table" rows={8} />;
  }

  if (error) {
    return <p className="text-red-400">Couldn't load prize history.</p>;
  }

  if (!data) return null;

  if (data.summary.totalPrizes === 0) {
    return <p className="text-gray-500">No prizes won yet.</p>;
  }

  const machineLabel = (code: string, name: string | null) =>
    name ? name.replace(/ Gacha$/, "") : code;

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <span className="text-gray-400">
          <span className="text-gray-100 font-semibold num">
            {data.summary.totalPrizes.toLocaleString()}
          </span>{" "}
          prizes ·{" "}
          <span className="text-gray-100 font-semibold num">
            {formatUsd(data.summary.totalValue)}
          </span>{" "}
          insured value
        </span>
        {data.summary.firstWinAt && data.summary.lastWinAt && (
          <span className="text-gray-500">
            {new Date(data.summary.firstWinAt).toLocaleDateString()} –{" "}
            {new Date(data.summary.lastWinAt).toLocaleDateString()}
          </span>
        )}
        {hasFilter && (
          <span className="text-yellow-400">
            {data.summary.filteredPrizes.toLocaleString()} match filters ·{" "}
            {formatUsd(data.summary.filteredValue)}
          </span>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-gray-400">
          Machine
          <select
            value={gachaCode}
            onChange={(e) => updateFilter(() => setGachaCode(e.target.value))}
            className="select min-w-44"
          >
            <option value="">All machines ({data.summary.totalPrizes})</option>
            {data.machines.map((m) => (
              <option key={m.code} value={m.code}>
                {machineLabel(m.code, m.name)} ({m.count})
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-400">
          From
          <input
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => updateFilter(() => setFrom(e.target.value))}
            className="select"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-400">
          To
          <input
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => updateFilter(() => setTo(e.target.value))}
            className="select"
          />
        </label>
        {hasFilter && (
          <button
            onClick={clearFilters}
            className="btn btn-ghost text-sm px-3 py-1.5"
          >
            Clear filters
          </button>
        )}
        <div className="ml-auto flex items-center gap-2 text-sm">
          <span className="text-gray-400">Per page</span>
          <div className="seg">
            {PAGE_SIZES.map((size) => (
              <button
                key={size}
                onClick={() => updateFilter(() => setLimit(size))}
                className={`seg-btn ${limit === size ? "seg-btn-on" : ""}`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      {data.data.length === 0 ? (
        <p className="text-gray-500 py-4">No prizes match these filters.</p>
      ) : (
        <div
          className={`overflow-x-auto transition-opacity ${
            isFetching ? "opacity-60" : ""
          }`}
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="table-head">
                <th className="pb-2 font-medium">Card</th>
                <th className="pb-2 font-medium text-right">Value</th>
                <th className="pb-2 pl-4 font-medium">Machine</th>
                <th className="pb-2 pl-4 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((prize) => (
                <tr
                  key={prize.id}
                  className="table-row"
                >
                  <td className="py-2">
                    <div className="text-gray-100">{prize.nftName}</div>
                    {(prize.nftGradingCompany || prize.nftGrade) && (
                      <div className="text-xs text-gray-500">
                        {[prize.nftGradingCompany, prize.nftGrade]
                          .filter(Boolean)
                          .join(" ")}
                      </div>
                    )}
                  </td>
                  <td className="py-2 text-right num text-gray-100">
                    {prize.insuredValue !== null
                      ? formatUsd(prize.insuredValue)
                      : "—"}
                  </td>
                  <td className="py-2 pl-4 text-gray-300">
                    {prize.gachaCode ? (
                      <button
                        onClick={() =>
                          updateFilter(() => setGachaCode(prize.gachaCode!))
                        }
                        className="font-mono text-xs text-blue-400 hover:text-blue-300 text-left"
                        title="Filter by this machine"
                      >
                        {prize.gachaCode}
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td
                    className="py-2 pl-4 text-gray-400 whitespace-nowrap"
                    title={new Date(prize.timestamp).toLocaleString()}
                  >
                    {new Date(prize.timestamp).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <span className="text-gray-400">
            Showing {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of{" "}
            {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="btn btn-secondary text-sm px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="First page"
            >
              «
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn btn-secondary text-sm px-3 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <span className="text-gray-400 px-1 num">
              Page {page} of {pageCount}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              disabled={page >= pageCount}
              className="btn btn-secondary text-sm px-3 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() => setPage(pageCount)}
              disabled={page >= pageCount}
              className="btn btn-secondary text-sm px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Last page"
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
